<template>
  <div class="phd-ach-form">
    <div class="ph-field-row">
      <label class="ph-field">
        <span class="ph-field-label">Routing number *</span>
        <input
          type="text"
          :value="local.transit_number"
          @input="updateAch('transit_number', $event.target.value)"
          class="ph-field-input"
          autocomplete="off"
          inputmode="numeric"
          maxlength="9"
          pattern="\d{9}"
          required
        />
      </label>
      <label class="ph-field">
        <span class="ph-field-label">Account number *</span>
        <input
          type="text"
          :value="local.account_number"
          @input="updateAch('account_number', $event.target.value)"
          class="ph-field-input"
          autocomplete="off"
          inputmode="numeric"
          required
        />
      </label>
    </div>

    <div class="ph-field-row">
      <label class="ph-field">
        <!-- "financial_instituation" — typo intentional. Mirrors the
             field name CompanyController::register reads at
             $request->ACH['financial_instituation'] (CompanyController.php:79).
             Renaming on the frontend without the matching backend rename
             will silently break ACH submissions. -->
        <span class="ph-field-label">Bank name *</span>
        <input
          type="text"
          :value="local.financial_instituation"
          @input="updateAch('financial_instituation', $event.target.value)"
          class="ph-field-input"
          required
        />
      </label>
      <label class="ph-field">
        <span class="ph-field-label">Branch *</span>
        <input
          type="text"
          :value="local.branch"
          @input="updateAch('branch', $event.target.value)"
          class="ph-field-input"
          required
        />
      </label>
    </div>

    <label class="ph-field ph-field--full">
      <span class="ph-field-label">Address *</span>
      <input
        type="text"
        :value="local.address_1"
        @input="updateBilling('address_1', $event.target.value)"
        class="ph-field-input"
        autocomplete="address-line1"
        required
      />
    </label>

    <div class="ph-field-row">
      <label class="ph-field">
        <span class="ph-field-label">City *</span>
        <input
          type="text"
          :value="local.city"
          @input="updateBilling('city', $event.target.value)"
          class="ph-field-input"
          autocomplete="address-level2"
          required
        />
      </label>
      <label class="ph-field">
        <span class="ph-field-label">State *</span>
        <input
          type="text"
          :value="local.state"
          @input="updateBilling('state', $event.target.value)"
          class="ph-field-input"
          autocomplete="address-level1"
          required
        />
      </label>
      <label class="ph-field">
        <span class="ph-field-label">ZIP *</span>
        <input
          type="text"
          :value="local.zip"
          @input="updateBilling('zip', $event.target.value)"
          class="ph-field-input"
          autocomplete="postal-code"
          inputmode="numeric"
          maxlength="10"
          required
        />
      </label>
    </div>

    <div v-if="error" class="ph-demo-error" role="alert" aria-live="assertive">{{ error }}</div>
  </div>
</template>

<script>
const ACH_FIELDS = ['transit_number', 'account_number', 'financial_instituation', 'branch'];
const BILLING_FIELDS = ['address_1', 'city', 'state', 'zip'];

export default {
  name: 'AchFieldsForm',

  // Single combined object — mirrors the legacy CompanyBillingInfo shape
  // where ACH and billing-address live alongside each other so the
  // backend `payment_type=ach` branch reads one payload.
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    error: { type: String, default: '' },
  },

  computed: {
    local() {
      return {
        transit_number: '',
        account_number: '',
        financial_instituation: '',
        branch: '',
        address_1: '',
        city: '',
        state: '',
        zip: '',
        ...(this.value || {}),
      };
    },
  },

  methods: {
    updateAch(field, val) {
      if (ACH_FIELDS.indexOf(field) === -1) return;
      // Strip non-digits for routing/account numbers — bank fields don't
      // accept punctuation and silently failing on submit reads as a bug.
      const cleaned = (field === 'transit_number' || field === 'account_number')
        ? String(val || '').replace(/\D/g, '')
        : val;
      this.$emit('input', { ...this.local, [field]: cleaned });
    },
    updateBilling(field, val) {
      if (BILLING_FIELDS.indexOf(field) === -1) return;
      this.$emit('input', { ...this.local, [field]: val });
    },
  },
};
</script>

<style lang="scss" scoped>
/* .ph-field-* rules are global via _ph-modal.scss imported from main.js.
   Only the form-specific overrides live here (3-col last row). */
.phd-ach-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 6px;
}

/* The City/State/ZIP row in ACH wants three columns on desktop. */
.phd-ach-form > .ph-field-row:last-of-type {
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.6fr) minmax(0, 0.6fr);
}

@media (max-width: 460px) {
  .phd-ach-form > .ph-field-row:last-of-type {
    grid-template-columns: 1fr;
  }
}
</style>
